import React from "react";
import { useFormFields } from "../../../../hooks/useFormFields";
import { useUpdateLoyaltyConfig } from "../../hooks/useUpdateLoyaltyConfig";
import { Input } from "../../../../components/common/input";
import { Button } from "../../../../components/common/button";
import type { LoyaltyConfigDto, UpdateLoyaltyConfigDto } from "../../../../api";

interface LoyaltyConfigFormProps {
  config: LoyaltyConfigDto;
}

export function LoyaltyConfigForm({ config }: LoyaltyConfigFormProps) {
  const { formFields, createChangeHandler } = useFormFields({
    pointsPer100: config.pointsPer100.toString(),
    redemptionValuePer100Points: (config.redemptionValuePer100Points ?? 1).toString(),
    validityDays: config.validityDays.toString(),
  });

  const buildUpdateDto = (): UpdateLoyaltyConfigDto => ({
    pointsPer100: Number(formFields.pointsPer100),
    redemptionValuePer100Points: Number(formFields.redemptionValuePer100Points),
    validityDays: Number(formFields.validityDays),
  });

  const { isLoading, errors, submitHandler } = useUpdateLoyaltyConfig(buildUpdateDto);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitHandler(e);
  };

  return (
    <form onSubmit={handleSubmit} className="w-full flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
        <Input
          label="Points Per 100 Currency Units"
          type="number"
          name="pointsPer100"
          value={formFields.pointsPer100}
          onChange={createChangeHandler("pointsPer100")}
          placeholder="1"
          required
          error={errors?.pointsPer100?.[0]}
          helperText="Number of loyalty points awarded per 100 QAR spent"
        />

        <Input
          label="Redemption Value Per 100 Points"
          type="number"
          step="0.01"
          min="0"
          name="redemptionValuePer100Points"
          value={formFields.redemptionValuePer100Points}
          onChange={createChangeHandler("redemptionValuePer100Points")}
          placeholder="1"
          required
          error={errors?.redemptionValuePer100Points?.[0]}
          helperText="QAR discount granted at checkout per 100 redeemed points"
        />

        <Input
          label="Validity Period (in days)"
          type="number"
          name="validityDays"
          value={formFields.validityDays}
          onChange={createChangeHandler("validityDays")}
          placeholder="365"
          required
          error={errors?.validityDays?.[0]}
          helperText="Number of days until loyalty points expire"
        />
      </div>

      <Button variant="filledDark" size="md" type="submit" fullWidth disabled={isLoading}>
        {isLoading ? "Updating…" : "Update Configuration"}
      </Button>
    </form>
  );
}
